import {faRotate} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {Button, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useNavigate, useParams} from 'react-router-dom'
import StatusButton from 'src/app/pages/orders/StatusButton'
import {PageLink, PageTitle} from 'src/marketConnect/layout/core'
import {useAppSelector} from 'src/redux/storeHooks'
import {ProductTableLite} from 'src/app/features/listing-product-table-v2'
import {
  CatalogColumn,
  FilterState,
  PaginationState,
  SelectedColumn,
  SortState,
} from 'src/app/features/listing-product-table-v2/types'
import {PayoutSettlementLink} from './PayoutSettlementLink'
import {getRowPlatformId} from './resolvePayoutPlatform'
import {payoutApiPost} from './api'
import {usePayoutLogsData} from './usePayoutLogsData'
import {normalizePayoutFilterState} from './buildPayoutFilterParams'
import {
  formatDate,
  getChannelName,
  getSettlementOrPayoutId,
  getSyncDocumentNo,
  normalizePayoutStatus,
  parseApiMessage,
} from './utils'

const PAYOUT_COLUMNS: CatalogColumn[] = [
  {
    id: 'settlement_payout_id',
    name: 'Settlement/Payout',
    field: 'settlement_payout_id',
    label: 'Settlement/Payout',
    headerName: 'Settlement/Payout',
    type: 'text',
    sortable: false,
    filterable: true,
    filterInputType: 'TEXT',
    filterOperators: ['contains'],
    resizable: false,
    width: 210,
  },
  {
    id: 'channel',
    name: 'Channel',
    field: 'channel',
    label: 'Channel',
    headerName: 'Channel',
    type: 'text',
    sortable: false,
    filterable: true,
    filterInputType: 'SELECT',
    resizable: false,
    width: 220,
  },
  {
    id: 'estimated_payout_at',
    name: 'Payout Date',
    field: 'estimated_payout_at',
    label: 'Payout Date',
    headerName: 'Payout Date',
    type: 'date',
    sortable: false,
    filterable: true,
    filterInputType: 'DATE_RANGE',
    resizable: false,
    width: 180,
  },
  {
    id: 'sync_status',
    name: 'Sync Status',
    field: 'sync_status',
    label: 'Sync Status',
    headerName: 'Sync Status',
    type: 'status',
    sortable: false,
    filterable: true,
    filterInputType: 'SELECT',
    filterOptions: [
      {label: 'Synced', value: 'SYNCED'},
      {label: 'Success', value: 'SUCCESS'},
      {label: 'Failed', value: 'FAILED'},
      {label: 'Error', value: 'ERROR'},
    ],
    resizable: false,
    width: 180,
  },
  {
    id: 'sync_doc',
    name: 'Sync Document No',
    field: 'sync_doc',
    label: 'Sync Document No',
    headerName: 'Sync Document No',
    type: 'text',
    sortable: false,
    filterable: true,
    filterInputType: 'TEXT',
    filterOperators: ['contains'],
    resizable: false,
    width: 180,
  },
]

const DEFAULT_SELECTED_COLUMNS: SelectedColumn[] = PAYOUT_COLUMNS.map((column, index) => ({
  field: column.field,
  order: index,
  width: column.width,
  pinned: null,
}))

const getComparableValue = (row: any, field: string) => {
  if (field === 'settlement_payout_id') return getSettlementOrPayoutId(row)
  if (field === 'channel') return getChannelName(row)
  if (field === 'sync_doc') return getSyncDocumentNo(row?.sync_doc || row?.qbo_check)
  return row?.[field]
}

const exportPayoutCSV = (rows: any[], selectedColumns: SelectedColumn[]) => {
  if (!rows.length) {
    alert('No data to export')
    return
  }

  const orderedColumns = [...selectedColumns].sort((a, b) => a.order - b.order)
  const headers = orderedColumns
    .map(
      (column) =>
        PAYOUT_COLUMNS.find((item) => item.field === column.field)?.headerName || column.field
    )
    .map((header) => `"${header}"`)
    .join(',')

  const body = rows.map((row) =>
    orderedColumns
      .map((column) => {
        const raw = getComparableValue(row, column.field)
        const value =
          column.field === 'estimated_payout_at'
            ? formatDate(raw)
            : raw === null || raw === undefined
            ? ''
            : String(raw)
        return `"${value.replace(/"/g, '""')}"`
      })
      .join(',')
  )

  const blob = new Blob([`\uFEFF${[headers, ...body].join('\n')}`], {
    type: 'text/csv;charset=utf-8;',
  })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `payout-logs-${new Date().toISOString().split('T')[0]}.csv`
  link.click()
  URL.revokeObjectURL(link.href)
}

export const PayoutLogsPage: FC = () => {
  const {platformId, id: routePlatformId} = useParams<{platformId: string; id: string}>()
  const navigate = useNavigate()
  const {fetchedConnectedPlatformData} = useAppSelector((state) => state.fetchConnectedPlatform)
  const [selectedPlatform, setSelectedPlatform] = useState<string>(routePlatformId || '')
  const effectivePlatformId = routePlatformId || selectedPlatform
  const [selectedColumns, setSelectedColumns] = useState<SelectedColumn[]>(DEFAULT_SELECTED_COLUMNS)
  const [sortState, setSortState] = useState<SortState | null>({
    field: 'estimated_payout_at',
    direction: 'desc',
  })
  const [filterState, setFilterState] = useState<FilterState>({globalSearch: '', columnFilters: []})
  const [resyncingId, setResyncingId] = useState<string | number | null>(null)
  const [paginationState, setPaginationState] = useState<PaginationState>({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  })

  const tableColumns = useMemo<CatalogColumn[]>(() => {
    const channelOptions = (fetchedConnectedPlatformData || [])
      .map((platform: any) => ({
        label: platform?.display_name || platform?.platform_info?.name || platform?.name,
        value: platform?.display_name || platform?.platform_info?.name || platform?.name,
      }))
      .filter((option) => Boolean(option.value))
      .sort((left, right) => String(left.label).localeCompare(String(right.label)))

    return PAYOUT_COLUMNS.map((column) => {
      if (column.field === 'channel' && channelOptions.length > 0) {
        return {...column, filterOptions: channelOptions}
      }
      return column
    })
  }, [fetchedConnectedPlatformData])

  const {data, total, loading, error, refetch} = usePayoutLogsData({
    platformId: effectivePlatformId,
    page: paginationState.page,
    pageSize: paginationState.pageSize,
    globalSearch: filterState.globalSearch,
    columnFilters: filterState.columnFilters,
    catalogColumns: tableColumns,
    sortState,
  })

  useEffect(() => {
    setSelectedPlatform(routePlatformId || '')
  }, [routePlatformId])

  const platformName = useMemo(() => {
    if (!effectivePlatformId) return 'All Platforms'
    const matchedPlatform = (fetchedConnectedPlatformData || []).find(
      (platform: any) => String(platform?.id) === String(effectivePlatformId)
    )
    return (
      matchedPlatform?.display_name ||
      matchedPlatform?.platform_info?.name ||
      matchedPlatform?.name ||
      'Platform'
    )
  }, [fetchedConnectedPlatformData, effectivePlatformId])

  const listReturnPath = useMemo(() => {
    if (!routePlatformId) return '/payout-logs'
    if (platformId) return `/payout-logs/${platformId}/${routePlatformId}`
    return '/payout-logs'
  }, [platformId, routePlatformId])

  const pageBreadcrumbs: PageLink[] = useMemo(
    () => [
      {title: 'Sales', path: '/orders-all', isActive: false},
      {title: 'Payout Logs', path: '/payout-logs', isActive: true},
    ],
    []
  )

  const tablePaginationState = useMemo(
    () => ({
      ...paginationState,
      total,
      totalPages: paginationState.pageSize > 0 ? Math.ceil(total / paginationState.pageSize) : 0,
    }),
    [paginationState, total]
  )

  const findConnectedPlatformByRow = useCallback(
    (row: any) => {
      const rowChannelId =
        row?.platform?.id || row?.platform_id || row?.channel_id || row?.id_platform
      const rowChannelName = String(getChannelName(row) || '').toLowerCase()
      return (fetchedConnectedPlatformData || []).find((platform: any) => {
        if (rowChannelId && String(platform?.id) === String(rowChannelId)) return true
        const names = [
          platform?.display_name,
          platform?.name,
          platform?.platform_info?.name,
          platform?.platform_info?.display_name,
        ]
        return (
          rowChannelName &&
          names.some((name) => String(name || '').toLowerCase() === rowChannelName)
        )
      })
    },
    [fetchedConnectedPlatformData]
  )

  const handleResync = useCallback(
    async (row: any) => {
      const payoutDate = row?.estimated_payout_at
      const platformIdForResync =
        effectivePlatformId ||
        findConnectedPlatformByRow(row)?.id ||
        getRowPlatformId(row)

      if (!platformIdForResync || !payoutDate) {
        return
      }

      const rowId =
        row?.id ||
        row?.grouped_payout_id ||
        row?.settlement_id ||
        row?.payout_id

      setResyncingId(rowId)

      try {
        await payoutApiPost('/api/resync-failed-payouts', {
          platform_id: platformIdForResync,
          payout_date: String(payoutDate).split('T')[0],
        })

        await refetch()
      } catch (error) {
        console.error('Error during payout resync:', error)
      } finally {
        setResyncingId(null)
      }
    },
    [effectivePlatformId, findConnectedPlatformByRow, refetch]
  )


  const handlePlatformChange = useCallback(
    (value: string) => {
      setSelectedPlatform(value)
      setPaginationState((prev) => ({...prev, page: 1}))

      if (!value) {
        navigate('/payout-logs')
        return
      }

      const matchedPlatform = (fetchedConnectedPlatformData || []).find(
        (platform: any) => String(platform?.id) === String(value)
      )
      const availablePlatformId = matchedPlatform?.platform_info?.id || platformId || ''
      if (availablePlatformId) {
        navigate(`/payout-logs/${availablePlatformId}/${value}`)
      }
    },
    [fetchedConnectedPlatformData, navigate, platformId]
  )

  return (
    <>
      <PageTitle breadcrumbs={pageBreadcrumbs}>Payout Logs</PageTitle>
      <div className='container-fluid px-0 py-2'>
        <div className='d-flex flex-column flex-md-row align-items-center justify-content-between mb-4'>
          <h1 className='text-dark fw-bold fs-5 my-0'>Payout Logs - {platformName}</h1>

          <div className='d-flex align-items-center mt-3 mt-md-0'>
            <label className='me-2 fw-semibold mb-0'>Platform:</label>
            <select
              className='form-select form-select-sm'
              aria-label='Select platform'
              value={selectedPlatform}
              onChange={(ev) => handlePlatformChange(ev.target.value)}
              style={{minWidth: 220}}
            >
              <option value=''>All Platforms</option>
              {fetchedConnectedPlatformData?.map((item: any) => (
                <option key={item?.id} value={item?.id}>
                  {item?.display_name || item?.platform_info?.name || item?.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div className='alert alert-danger d-flex align-items-center mb-4' role='alert'>
            <i className='bi bi-exclamation-triangle-fill me-3 fs-3'></i>
            <div>{error}</div>
          </div>
        )}

        <ProductTableLite
          data={data}
          loading={loading}
          catalogColumns={tableColumns}
          selectedColumns={selectedColumns}
          onColumnsChange={setSelectedColumns}
          onReorderColumns={(dragIndex, hoverIndex) => {
            setSelectedColumns((previous) => {
              const next = [...previous].sort((a, b) => a.order - b.order)
              const [removed] = next.splice(dragIndex, 1)
              next.splice(hoverIndex, 0, removed)
              return next.map((column, index) => ({...column, order: index}))
            })
          }}
          onPinColumn={(field, position) => {
            setSelectedColumns((previous) =>
              previous.map((column) =>
                column.field === field ? {...column, pinned: position} : column
              )
            )
          }}
          getColumnByField={(field) => tableColumns.find((column) => column.field === field)}
          sortState={sortState}
          onSortChange={(field, direction) => {
            setSortState({field, direction})
            setPaginationState((prev) => ({...prev, page: 1}))
          }}
          filterState={filterState}
          onFilterChange={(filters) => {
            setFilterState(normalizePayoutFilterState(filters, tableColumns))
            setPaginationState((prev) => ({...prev, page: 1}))
          }}
          paginationState={tablePaginationState}
          onPageChange={(page) => setPaginationState((prev) => ({...prev, page}))}
          onPageSizeChange={(pageSize) =>
            setPaginationState((prev) => ({...prev, pageSize, page: 1}))
          }
          onRefresh={refetch}
          onExportCSV={() => exportPayoutCSV(data, selectedColumns)}
          onResetColumns={() => {
            setSelectedColumns(DEFAULT_SELECTED_COLUMNS)
            setSortState({field: 'estimated_payout_at', direction: 'desc'})
            setFilterState({globalSearch: '', columnFilters: []})
            setPaginationState((prev) => ({...prev, page: 1}))
          }}
          mandatoryColumns={['settlement_payout_id']}
          defaultVisibleFields={PAYOUT_COLUMNS.map((column) => column.field)}
          leftAlignFields={['sync_status']}
          customCellRenderers={{
            settlement_payout_id: (row) => (
              <PayoutSettlementLink
                row={row}
                connectedPlatforms={fetchedConnectedPlatformData || []}
                routePlatformId={routePlatformId}
                returnTo={listReturnPath}
              />
            ),
            channel: (row) => {
              const matchedPlatform = findConnectedPlatformByRow(row)
              const logo =
                row?.platform?.platform_info?.logo_ref ||
                row?.platform_info?.logo_ref ||
                row?.platform?.logo_ref ||
                matchedPlatform?.platform_info?.logo_ref
              const name =
                getChannelName(row) ||
                matchedPlatform?.display_name ||
                matchedPlatform?.platform_info?.name ||
                'N/A'
              return (
                <div className='d-flex align-items-center'>
                  {logo ? (
                    <img
                      src={logo}
                      alt=''
                      style={{width: '20px', height: '20px'}}
                      className='me-1 rounded'
                    />
                  ) : null}
                  <span>{name}</span>
                </div>
              )
            },
            estimated_payout_at: (row) => <span>{formatDate(row?.estimated_payout_at)}</span>,
            sync_status: (row) => {
            const status = normalizePayoutStatus(row?.sync_status)
            const rowId =
              row?.id ||
              row?.grouped_payout_id ||
              row?.settlement_id ||
              row?.payout_id

            const canResync = status === 'FAILED' || status === 'ERROR'
            const isResyncing = resyncingId === rowId

            return (
              <div className='d-inline-flex align-items-center'>
                <StatusButton
                  syncStatus={status || 'N/A'}
                  syncMessage={parseApiMessage(row?.api_message) || undefined}
                />

                {canResync && (
                  <OverlayTrigger
                    placement='top'
                    overlay={
                      <Tooltip id={`tooltip-${rowId}`}>Click to retry syncing the payout</Tooltip>
                    }
                  >
                    <Button
                      type='button'
                      className='ms-2'
                      variant='link'
                      style={{
                        padding: '5px 10px',
                        cursor: isResyncing ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => handleResync(row)}
                      disabled={isResyncing}
                      aria-label={isResyncing ? 'Resyncing...' : 'Resync'}
                    >
                      <FontAwesomeIcon icon={faRotate} color='red' size='lg' />
                    </Button>
                  </OverlayTrigger>
                )}
              </div>
            )
          },
            sync_doc: (row) => <span>{getSyncDocumentNo(row?.sync_doc || row?.qbo_check)}</span>,
          }}
        />
      </div>
    </>
  )
}
